import React, { useEffect, useState, useRef } from 'react';
import { X, Gift } from 'lucide-react';
import { SpinWheelRewards } from './MaschaSkinsData';
interface SpinTheWheelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRewardClaimed: (reward: any) => void;
  spinsAvailable: number;
  onSpinUsed: () => void;
}
export const SpinTheWheelModal: React.FC<SpinTheWheelModalProps> = ({
  isOpen,
  onClose,
  onRewardClaimed,
  spinsAvailable,
  onSpinUsed
}) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinDegrees, setSpinDegrees] = useState(0);
  const [selectedReward, setSelectedReward] = useState<any>(null);
  const [showReward, setShowReward] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSpinning(false);
      setSelectedReward(null);
      setShowReward(false);
      setSpinDegrees(0);
    }
  }, [isOpen]);
  const handleSpin = () => {
    if (isSpinning || spinsAvailable <= 0) return;
    setIsSpinning(true);
    setShowReward(false);
    // Determine the winning reward based on probabilities
    const totalProbability = SpinWheelRewards.reduce((sum, reward) => sum + reward.probability, 0);
    const random = Math.random() * totalProbability;
    let cumulativeProbability = 0;
    let winningReward = SpinWheelRewards[0];
    for (const reward of SpinWheelRewards) {
      cumulativeProbability += reward.probability;
      if (random <= cumulativeProbability) {
        winningReward = reward;
        break;
      }
    }
    // Calculate the degrees to spin
    // Each segment is 360 / number of rewards degrees
    const segmentDegrees = 360 / SpinWheelRewards.length;
    // Find the index of the winning reward
    const winningIndex = SpinWheelRewards.findIndex(r => r.id === winningReward.id);
    // Calculate the final position
    // We want to land in the middle of the segment, so add half a segment
    const landingPosition = 360 - (winningIndex * segmentDegrees + segmentDegrees / 2);
    // Add multiple full rotations plus the landing position
    const totalDegrees = 1800 + landingPosition;
    setSpinDegrees(totalDegrees);
    setSelectedReward(winningReward);
    // After spinning is complete
    setTimeout(() => {
      setIsSpinning(false);
      setShowReward(true);
      onSpinUsed();
    }, 5000);
  };
  const handleClaimReward = () => {
    if (selectedReward) {
      onRewardClaimed(selectedReward);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-20535">
      <div className="relative w-full max-w-lg bg-gradient-to-b from-purple-900/50 to-black rounded-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-purple-800/30" data-id="element-20536">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={onClose} data-id="element-20537">
          <X size={18} data-id="element-20538" />
        </button>
        <div className="p-6" data-id="element-20539">
          {/* Header */}
          <div className="text-center mb-6" data-id="element-20540">
            <div className="flex items-center justify-center mb-2" data-id="element-20541">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center" data-id="element-20542">
                <Gift size={24} className="text-white" data-id="element-20543" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-white" data-id="element-20544">Spin the Wheel</h2>
            <p className="text-gray-300 text-sm" data-id="element-20545">
              Try your luck to win exclusive rewards!
            </p>
          </div>
          {/* Wheel section */}
          {!showReward ? <div className="relative flex flex-col items-center justify-center mb-6" data-id="element-20546">
              {/* Wheel marker */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-20547">
                <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-pink-600" data-id="element-20548"></div>
              </div>
              {/* Wheel */}
              <div className="relative w-64 h-64 mx-auto" data-id="element-20549">
                <div ref={wheelRef} className="w-full h-full rounded-full border-4 border-purple-800 transition-transform duration-5000 ease-out" style={{
              transform: `rotate(${spinDegrees}deg)`,
              backgroundImage: 'conic-gradient(from 0deg, #4c1d95, #7e22ce, #a21caf, #c026d3, #db2777, #4c1d95)'
            }} data-id="element-20550">
                  {/* Wheel segments */}
                  {SpinWheelRewards.map((reward, index) => {
                const segmentDegrees = 360 / SpinWheelRewards.length;
                const rotation = index * segmentDegrees;
                return <div key={reward.id} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center" style={{
                  transform: `rotate(${rotation}deg)`
                }} data-id="element-20551">
                        <div className="absolute top-7 left-1/2 transform -translate-x-1/2 text-white font-bold text-lg" style={{
                    textShadow: '0 0 3px black'
                  }} data-id="element-20552">
                          {reward.icon}
                        </div>
                        <div className="absolute top-0 left-1/2 w-px h-full bg-purple-800/70" style={{
                    transform: 'translateX(-50%)'
                  }} data-id="element-20553"></div>
                      </div>;
              })}
                </div>
              </div>
              {/* Spin button */}
              <button onClick={handleSpin} disabled={isSpinning || spinsAvailable <= 0} className={`mt-6 px-6 py-3 rounded-lg text-white font-medium ${isSpinning || spinsAvailable <= 0 ? 'bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/20 animate-pulse hover:animate-none'} transition-all`} data-id="element-20554">
                {isSpinning ? 'Spinning...' : spinsAvailable <= 0 ? 'No Spins Left' : `Spin Now (${spinsAvailable})`}
              </button>
            </div> :
        // Reward reveal
        <div className="text-center py-6 animate-in fade-in zoom-in duration-300" data-id="element-20555">
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mb-4" data-id="element-20556">
                <span className="text-4xl" data-id="element-20557">{selectedReward?.icon}</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-20558">
                {selectedReward?.name}
              </h3>
              <p className="text-gray-300 mb-6" data-id="element-20559">
                {selectedReward?.type === 'nothing' ? 'Better luck next time!' : `You won ${selectedReward?.name}!`}
              </p>
              <button onClick={handleClaimReward} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all" data-id="element-20560">
                {selectedReward?.type === 'nothing' ? 'Try Again Later' : 'Claim Reward'}
              </button>
            </div>}
          {/* Available spins */}
          <div className="mt-4 text-center" data-id="element-20561">
            <p className="text-gray-400 text-sm" data-id="element-20562">
              {spinsAvailable} spin{spinsAvailable !== 1 ? 's' : ''} remaining
            </p>
          </div>
        </div>
      </div>
      <style jsx data-id="element-20563">{`
        @keyframes subtle-pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        .animate-subtle-pulse {
          animation: subtle-pulse 2s infinite;
        }
        .duration-5000 {
          transition-duration: 5000ms;
        }
      `}</style>
    </div>;
};